import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import React, { useCallback, useState } from 'react';

import { PASSWORD_MANAGER_IGNORE_ATTRIBUTES } from '../../../types/forms';
import { FieldSaveIndicator, TFieldSaveState } from '../_components_/FieldSaveIndicator';

type TProps = Omit<TextFieldProps, 'type' | 'slotProps'> & {
  label: string;
  dataAction?: string;
  hideToggleWhenEmpty?: boolean;
  alwaysShrinkLabel?: boolean;
  saveState?: TFieldSaveState;
  reserveIndicatorSpace?: boolean;
};

const INDICATOR_SPACE = 48;
const INDICATOR_RIGHT_WITHOUT_TOGGLE = 14;
const INDICATOR_RIGHT_WITH_TOGGLE = 56;

const keepInputFocus = (e: React.MouseEvent) => e.preventDefault();

export const SecretTextField: React.FC<TProps> = ({
  label,
  dataAction,
  hideToggleWhenEmpty = false,
  alwaysShrinkLabel = false,
  saveState,
  reserveIndicatorSpace = false,
  ...textFieldProps
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = useCallback(() => setIsVisible((visible) => !visible), []);

  const hasValue = Boolean(textFieldProps.value);
  const isToggleShown = !hideToggleWhenEmpty || hasValue;

  return (
    <TextField
      {...textFieldProps}
      label={label}
      type={isVisible ? 'text' : 'password'}
      autoComplete="new-password"
      fullWidth
      slotProps={{
        inputLabel: alwaysShrinkLabel ? { shrink: true } : undefined,
        htmlInput: {
          ...PASSWORD_MANAGER_IGNORE_ATTRIBUTES,
          'data-action': dataAction,
          style: reserveIndicatorSpace ? { paddingRight: INDICATOR_SPACE } : undefined,
        },
        input: {
          endAdornment: (
            <>
              <FieldSaveIndicator
                state={saveState}
                right={isToggleShown ? INDICATOR_RIGHT_WITH_TOGGLE : INDICATOR_RIGHT_WITHOUT_TOGGLE}
              />
              {isToggleShown && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label={isVisible ? `Hide ${label}` : `Show ${label}`}
                    aria-pressed={isVisible}
                    onMouseDown={keepInputFocus}
                    onClick={toggleVisibility}
                  >
                    {isVisible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              )}
            </>
          ),
        },
      }}
    />
  );
};

export default SecretTextField;
